/**
 * Shared online/offline state for the app.
 *
 * PROBLEM: `navigator.onLine` can return `true` even when the network is
 * unreachable (browser hasn't updated its OS connectivity state yet, or the
 * page was served from the Service Worker cache). Relying on it at initial
 * render causes OfflineSafeRedirect to send unmatched routes to `/login`
 * before anything has a chance to detect the actual offline state.
 *
 * SOLUTION: default to OFFLINE and only switch to online when something
 * PROVES connectivity (e.g., Google Maps LoadScript fires onLoad).
 * This eliminates the race condition entirely.
 *
 * USED BY:
 * - `MapsLoader` (main.tsx) — calls `proveOnline()` when LoadScript succeeds
 * - `OfflineSafeRedirect` (router.tsx) — reads `isDefinitelyOnline()` to
 *   decide where to redirect unmatched routes
 */

// Default to offline on every page load. Only switch to online when a backend
// request succeeds. This prevents false positives when WiFi is connected but
// backend is unreachable (network partition, backend down, etc).
let _provenOnline = false;

/** Marks the app as "definitely online" — call when a BACKEND request succeeds. */
export function proveOnline(): void {
  _provenOnline = true;
}

/** Marks the app as offline — call when a BACKEND request fails with network error. */
export function proveOffline(): void {
  _provenOnline = false;
}

/**
 * Returns `true` only when BOTH conditions are met:
 *   1. Something has proven we have actual network access
 *   2. `navigator.onLine` also agrees
 *
 * Without the first check, `navigator.onLine` can be stale at page load,
 * causing false positives when the device has a network interface up but
 * no actual internet connectivity.
 */
export function isDefinitelyOnline(): boolean {
  return _provenOnline && navigator.onLine;
}
